import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy, useMemo } from "react";
import { ROUTES } from "./paths";
import PrivateRoute from "components/PrivateRoute";
import MainLayout from "./layouts/MainLayout";
import AuthLayout from "./layouts/AuthLayout";

const LoginPage = lazy(() => import("pages/LoginPage"));
const Dashboard = lazy(() => import("pages/Dashboard"));
const Policonsultorio = lazy(() => import("pages/PoliconsultorioPage"));
const Sucursal = lazy(() => import("pages/SucursalPage"));
const Comercio = lazy(() => import("pages/ComercioPage"));
const Beneficio = lazy(() => import("pages/BeneficioPage"));
const Banner = lazy(() => import("pages/BannerPage"));
const Canjes = lazy(() => import("pages/CanjesPage"));
const Intereses = lazy(() => import("pages/InteresesPage"));
const Planes = lazy(() => import("pages/PlanesPage"));
const Notificaciones = lazy(() => import("pages/NotificacionesPage"));
const Colaboradores = lazy(() => import("pages/ColaboradoresPage"));
const Administradores = lazy(() => import("pages/AdministradoresPage"));
const Configuracion = lazy(() => import("pages/ConfiguracionPage"));
const Usuarios = lazy(() => import("pages/UsuariosPage"));
const WebBannersPage = lazy(() => import("pages/WebBannersPage"));
const WebDatosInstitPage = lazy(() => import("pages/WebDatosInstitPage"));
// const NotFound = lazy(() => import("pages/NotFound")); // opcional

export default function AppRouter() {
    const permisos = useMemo(() => {
        try {
            const raw = localStorage.getItem("usuario");
            const user = raw ? JSON.parse(raw) : null;
            const roles = user?.roles || [];
            const sucursales = user?.sucursales || [];
            const comercios = user?.comercios || [];
            const esAdmin = roles.some((r) => (r.nombre || "").toLowerCase() === "administrador");
            const permisosSet = new Set(
                roles.flatMap((rol) => {
                    const rawPerms = Array.isArray(rol?.permisos)
                        ? rol.permisos
                        : Array.isArray(rol?.rol_permisos)
                            ? rol.rol_permisos
                            : [];
                    return rawPerms
                        .map((perm) => {
                            if (typeof perm === "string") return perm;
                            if (perm?.nombre) return perm.nombre;
                            if (perm?.permiso?.nombre) return perm.permiso.nombre;
                            return null;
                        })
                        .filter(Boolean)
                        .map((perm) => String(perm).trim().toLowerCase());
                })
            );
            const canUsuariosApp = permisosSet.has("usuario.listar");
            const canColaboradores = permisosSet.has("colaboradores.listar");
            const canAdministradores = permisosSet.has("administradores.listar");
            const canCanjes = permisosSet.has("canjes.listar");
            const tieneSucursalPropia = Array.isArray(sucursales) && sucursales.length > 0;
            const tieneSucursalDeComercio =
                Array.isArray(comercios) &&
                comercios.some((c) => Array.isArray(c.sucursales) && c.sucursales.length > 0);
            const puedeVerEmeClub = tieneSucursalPropia || tieneSucursalDeComercio;
            return {
                esAdmin,
                canUsuariosApp,
                canColaboradores,
                canAdministradores,
                canCanjes,
                tieneSucursalPropia,
                tieneSucursalDeComercio,
                puedeVerEmeClub,
            };
        } catch {
            return {
                esAdmin: false,
                canUsuariosApp: false,
                canColaboradores: false,
                canAdministradores: false,
                canCanjes: false,
                tieneSucursalPropia: false,
                tieneSucursalDeComercio: false,
                puedeVerEmeClub: false,
            };
        }
    }, []);

    const guard = (condition, element) => (condition ? element : <Navigate to={ROUTES.DASHBOARD} replace />);

    return (
        <BrowserRouter>
            <Suspense fallback={<div className="p-4">Cargando…</div>}>

                <Routes>
                    {/* Público (sin navbar) */}
                    <Route element={<AuthLayout />}>
                        <Route path={ROUTES.ROOT} element={<LoginPage />} />
                    </Route>

                    {/* Privado (con navbar) */}
                    <Route element={<PrivateRoute />}>
                        <Route element={<MainLayout />}>
                            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                            <Route
                                path={ROUTES.POLICONSULTORIO}
                                element={guard(permisos.tieneSucursalPropia, <Policonsultorio />)}
                            />
                            <Route path={ROUTES.SUCURSALES} element={guard(permisos.esAdmin, <Sucursal />)} />
                            <Route
                                path={ROUTES.EME_COMERCIOS}
                                element={guard(permisos.tieneSucursalPropia, <Comercio />)}
                            />
                            <Route
                                path={ROUTES.EME_BENEFICIOS}
                                element={guard(permisos.tieneSucursalPropia, <Beneficio />)}
                            />
                            <Route
                                path={ROUTES.EME_BANNERS}
                                element={guard(permisos.tieneSucursalPropia, <Banner />)}
                            />
                            <Route
                                path={ROUTES.EME_CANJES}
                                element={guard(permisos.tieneSucursalDeComercio && permisos.canCanjes, <Canjes />)}
                            />
                            <Route
                                path={ROUTES.EME_INTERESES}
                                element={guard(permisos.tieneSucursalPropia, <Intereses />)}
                            />
                            <Route
                                path={ROUTES.EME_PLANES}
                                element={guard(permisos.esAdmin, <Planes />)}
                            />
                            <Route
                                path={ROUTES.EME_NOTIFICACIONES}
                                element={guard(permisos.tieneSucursalPropia, <Notificaciones />)}
                            />
                            <Route
                                path={ROUTES.COLABORADORES}
                                element={guard(permisos.esAdmin || permisos.canColaboradores, <Colaboradores />)}
                            />
                            <Route
                                path={ROUTES.ADMINISTRADORES}
                                element={guard(permisos.esAdmin || permisos.canAdministradores, <Administradores />)}
                            />
                            <Route path={ROUTES.CONFIGURACION} element={<Configuracion />} />
                            <Route
                                path={ROUTES.USUARIOS}
                                element={guard(permisos.esAdmin || permisos.canUsuariosApp, <Usuarios />)}
                            />
                            <Route path={ROUTES.WEB_EME_BANNERS} element={<WebBannersPage />} />
                            <Route path={ROUTES.WEB_EME_DATOS_INSTIT} element={<WebDatosInstitPage />} />
                        </Route>
                    </Route>

                    {/* 404 opcional */}
                    {/* <Route path="*" element={<NotFound />} /> */}
                </Routes>

            </Suspense>
        </BrowserRouter>
    );
}
